import { useRef, useState, type DragEvent } from 'react';
import { AlertTriangle, FileText, Loader2, Paperclip, X } from 'lucide-react';

import { InfoTip } from '../FieldLabel';
import { formatBytes, typeFromName, typeLabel, type UploadedFile } from './attachmentFiles';
import type { AttachmentDraft, Draft } from './useAttachmentDraft';

type PickedFile = Draft<UploadedFile>;

function zoneHint(draft: AttachmentDraft, compact: boolean): string {
  const { rules } = draft;
  if (draft.full) return `Up to ${rules.maxFiles} files.`;
  if (!compact) return rules.hint;
  return rules.screenshots ? 'or paste a screenshot' : 'or drop files here';
}

function DropZone({
  draft,
  id,
  compact,
  label,
  describedBy,
  hint,
}: {
  draft: AttachmentDraft;
  id: string;
  compact: boolean;
  label: string;
  describedBy?: string;
  hint?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const { rules } = draft;

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setOver(false);
    draft.add(Array.from(event.dataTransfer.files));
  }

  return (
    <div
      className={`attach-zone${over ? ' is-over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={draft.full}
        title={compact ? rules.hint : undefined}
        aria-describedby={hint ? `${id}-tip` : undefined}
        onClick={() => input.current?.click()}
      >
        <Paperclip aria-hidden="true" /> {label}
      </button>
      {hint && (
        <InfoTip id={`${id}-tip`} about={label}>
          {hint}
        </InfoTip>
      )}
      <span className="hint" id={`${id}-hint`}>
        {zoneHint(draft, compact)}
      </span>
      <input
        ref={input}
        id={id}
        type="file"
        multiple
        accept={rules.accept}
        className="sr-only"
        tabIndex={-1}
        aria-label={compact ? label : undefined}
        aria-describedby={[`${id}-hint`, hint && `${id}-tip`, describedBy].filter(Boolean).join(' ')}
        onChange={(e) => {
          draft.add(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </div>
  );
}

function RemoveButton({ file, className, onRemove }: { file: PickedFile; className: string; onRemove: () => void }) {
  return (
    <button type="button" className={className} aria-label={`Remove ${file.name}`} data-tooltip="Remove" onClick={onRemove}>
      <X aria-hidden="true" />
    </button>
  );
}

function TableStatus({ file }: { file: PickedFile }) {
  if (file.state === 'uploading') {
    return (
      <span className="hint" role="status">
        <Loader2 aria-hidden="true" className="spin" /> Uploading…
      </span>
    );
  }
  if (file.state === 'error') return <span className="attach-error" role="alert">{file.error}</span>;
  return <>{formatBytes(file.size)}</>;
}

function ChipStatus({ file }: { file: PickedFile }) {
  if (file.state === 'uploading') {
    return (
      <>
        <Loader2 aria-hidden="true" className="spin" /> Uploading…
      </>
    );
  }
  if (file.state === 'error') return <span className="attach-error" role="alert">{file.error}</span>;
  return <>{formatBytes(file.size)}</>;
}

function ChipThumb({ file }: { file: PickedFile }) {
  if (file.preview) return <img src={file.preview} alt="" />;
  return file.state === 'error' ? <AlertTriangle /> : <FileText />;
}

function PickedTable({ draft }: { draft: AttachmentDraft }) {
  return (
    <div className="table-scroll attach-table-wrap">
      <table className="data compact attach-table" aria-label="Attached files">
        <thead>
          <tr>
            <th scope="col">File</th>
            <th scope="col">Type</th>
            <th scope="col" className="num">Size</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {draft.drafts.map((d) => (
            <tr key={d.key} className={`is-${d.state}`}>
              <td className="attach-table-name">
                <span className="attach-table-file">
                  {d.state === 'error' ? <AlertTriangle aria-hidden="true" /> : <FileText aria-hidden="true" />}
                  <span title={d.name}>{d.name}</span>
                </span>
              </td>
              <td className="hint">{d.attachment ? typeLabel(d.attachment.content_type) : typeFromName(d.name)}</td>
              <td className="num">
                <TableStatus file={d} />
              </td>
              <td>
                <div className="row-actions">
                  <RemoveButton file={d} className="btn btn-secondary btn-icon" onRemove={() => draft.discard(d.key)} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PickedChips({ draft }: { draft: AttachmentDraft }) {
  return (
    <ul className="attach-list" aria-label="Attached files">
      {draft.drafts.map((d) => (
        <li key={d.key} className={`attach-chip is-${d.state}`}>
          <span className="attach-thumb" aria-hidden="true">
            <ChipThumb file={d} />
          </span>
          <span className="attach-text">
            <span className="attach-name" title={d.name}>{d.name}</span>
            <span className="hint">
              <ChipStatus file={d} />
            </span>
          </span>
          <RemoveButton file={d} className="btn btn-ghost btn-icon attach-remove" onRemove={() => draft.discard(d.key)} />
        </li>
      ))}
    </ul>
  );
}

export function AttachmentPicker({
  draft,
  id,
  compact = false,
  label = 'Attach files',
  layout = 'chips',
  describedBy,
  hint,
}: {
  draft: AttachmentDraft;
  id: string;
  compact?: boolean;
  label?: string;
  /** How picked files show: chips in a grid (Feedback), or rows of a table. */
  layout?: 'chips' | 'table';
  /** The id of text that also describes the file input (a rule the picker does not know). */
  describedBy?: string;
  /** How-to-use text shown as an info tip beside the button. */
  hint?: string;
}) {
  const hasFiles = draft.drafts.length > 0;
  return (
    <div className={`attach${compact ? ' attach-compact' : ''}`}>
      <DropZone draft={draft} id={id} compact={compact} label={label} describedBy={describedBy} hint={hint} />
      {hasFiles && layout === 'table' && <PickedTable draft={draft} />}
      {hasFiles && layout === 'chips' && <PickedChips draft={draft} />}
    </div>
  );
}
